import React, { useState } from "react";
import { Button, Tooltip, Space, Divider, Dropdown, Modal } from "antd";
import { DeleteOutlined, DownOutlined } from "@ant-design/icons";
import { getSymbolsForLayout, SYMBOL_CATEGORIES } from "../utils/symbolTypes";

const SymbolPalette = ({
  layoutType,
  selectedSymbolType,
  symbolPlacingMode,
  onSymbolSelect,
  onClearSelection,
  placedSymbolsCount,
  placedSymbols,
  onDeleteAll,
  onDeleteByCategory,
}) => {
  const availableSymbols = getSymbolsForLayout(layoutType);
  
  const symbolsByCategory = availableSymbols.reduce((acc, symbol) => {
    if (!acc[symbol.category]) {
      acc[symbol.category] = [];
    }
    acc[symbol.category].push(symbol);
    return acc;
  }, {});

  const getSymbolCountByCategory = (category) => {
    return placedSymbols.filter((s) => {
      const symbolType = getSymbolsForLayout(layoutType).find(
        (st) => st.id === s.symbol_type
      );
      return symbolType?.category === category;
    }).length;
  };

  const handleDeleteByCategory = (category) => {
    const count = getSymbolCountByCategory(category);
    if (count === 0) {
      Modal.info({ title: "提示", content: "此類型沒有物件" });
      return;
    }

    Modal.confirm({
      title: `確定要刪除所有 ${count} 個「${category}」物件標記嗎？`,
      content: "此操作無法復原",
      okText: "確定",
      cancelText: "取消",
      okType: "danger",
      onOk: () => {
        onDeleteByCategory(category);
      },
    });
  };

  const handleDeleteAll = () => {
    Modal.confirm({
      title: `確定要刪除所有 ${placedSymbolsCount} 個物件標記嗎？`,
      content: "此操作無法復原",
      okText: "確定",
      cancelText: "取消",
      okType: "danger",
      onOk: onDeleteAll,
    });
  };

  const menuItems = Object.entries(SYMBOL_CATEGORIES)
    .filter(([_, value]) => getSymbolCountByCategory(value) > 0)
    .map(([key, value]) => ({
      key: `category-${value}`,
      label: `移除「${value}」(${getSymbolCountByCategory(value)})`,
      danger: true,
      onClick: () => handleDeleteByCategory(value),
    }));

  return (
    <div>
      {placedSymbolsCount > 0 && (
        <>
          <Space.Compact block style={{ marginBottom: 12 }}>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ flex: 1 }}
              onClick={handleDeleteAll}
            >
              移除全部 ({placedSymbolsCount})
            </Button>
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={["click"]}
              disabled={menuItems.length === 0}
            >
              <Button danger size="small" icon={<DownOutlined />} />
            </Dropdown>
          </Space.Compact>

          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      <div style={{ fontSize: 12, color: "#666" }}>
        {selectedSymbolType
          ? symbolPlacingMode === "single"
            ? "點擊畫布放置物件（放置後自動取消）"
            : "點擊畫布持續放置物件（多次放置模式）"
          : "選擇物件類型以開始放置"}
      </div>

      <div
        style={{
          marginBottom: 12, 
          fontSize: 11,
          color: "#999",
          lineHeight: 1.4,
        }}
      >
        提示: 點擊物件一次進入單次放置模式，點擊兩次進入連續放置模式。拖動物件可調整位置。
      </div>

      {Object.entries(symbolsByCategory).map(([category, symbols]) => (
        <div key={category} style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              color: "#999",
              marginBottom: 6,
              fontWeight: 500,
            }}
          >
            {category}
          </div>
          <Space wrap size={4}>
            {symbols.map((symbol) => {
              const isSelected = selectedSymbolType === symbol.id;
              const baseStyle = {
                width: 36,
                height: 36,
                padding: 0,
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderWidth: isSelected ? 3 : 1,
                transform: isSelected ? "scale(1.15)" : "scale(1)",
                transition: "all 0.2s ease",
                zIndex: isSelected ? 10 : 1,
                position: "relative",
                outline: "none",
              };

              const selectedStyle = isSelected
                ? symbolPlacingMode === "multiple"
                  ? {
                      backgroundColor: symbol.color,
                      borderColor: symbol.color,
                      boxShadow: `0 0 16px ${symbol.color}, 0 0 0 3px #fff, 0 0 0 6px ${symbol.color}`,
                    }
                  : {
                      backgroundColor: symbol.color,
                      borderColor: symbol.color,
                      boxShadow: `0 0 12px ${symbol.color}, 0 0 0 2px #fff, 0 0 0 4px ${symbol.color}`,
                    }
                : {
                    borderColor: symbol.color,
                  };

              const IconComponent = symbol.icon;

              return (
                <Tooltip key={symbol.id} title={symbol.name} placement="top">
                  <Button
                    size="small"
                    type={isSelected ? "primary" : "default"}
                    style={{
                      ...baseStyle,
                      ...selectedStyle,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSymbolSelect(symbol.id)}
                  >
                    {IconComponent && <IconComponent size={18} />}
                  </Button>
                </Tooltip>
              );
            })}
          </Space>
        </div>
      ))}

      {selectedSymbolType && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <Button
            block
            size="small"
            onClick={onClearSelection}
            icon={<DeleteOutlined />}
          >
            取消選擇{" "}
            <span style={{ opacity: 0.6, fontSize: 11 }}>(ESC)</span>
          </Button>
        </>
      )}
    </div>
  );
};

export default SymbolPalette;



